import { useNavigate } from '@tanstack/react-router';
import { KeyRound, Loader2, ShieldCheck } from 'lucide-react';
import { useRef, useState, type FormEvent } from 'react';

import { AuthCard } from '@/components/layout/AuthCard';
import { Button } from '@/components/ui/button';
import { FormAlert } from '@/components/ui/form-alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { genericErrorMessage, httpStatus, serverMessage } from '@/lib/api-errors';
import { authApi } from '@/lib/auth-api';
import { CHANGE_PASSWORD_PATH } from '@/routes/change-password';
import { useAuthStore } from '@/stores/auth';
import { useChallenge } from '@/stores/challenge';

export type MfaCodeKind = 'totp' | 'backup';

/** 6 chiffres purs = TOTP ; le reste est traité comme un code de secours (XXXX-XXXX, tiret et espaces tolérés). */
export const detectCodeKind = (code: string): MfaCodeKind => (/^\d{6}$/.test(code.trim()) ? 'totp' : 'backup');

const BACKUP_RE = /^[A-Z0-9]{4}[\s-]?[A-Z0-9]{4}$/i;

export const validateCode = (code: string): string | null => {
  const value = code.trim();
  if (!value) return 'Saisissez le code';
  if (detectCodeKind(value) === 'totp' || BACKUP_RE.test(value)) return null;
  return /^\d+$/.test(value) ? 'Le code TOTP comporte 6 chiffres' : 'Code de secours attendu au format XXXX-XXXX';
};

const EXPIRED_CHALLENGE = 'challenge invalide ou expiré';

export const MfaVerifyPage = () => {
  const navigate = useNavigate();
  const { challengeToken, email, clearChallenge } = useChallenge();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [code, setCode] = useState('');
  const [backupMode, setBackupMode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const backToLogin = (expired: boolean) => {
    clearChallenge();
    void navigate({ to: '/login', search: expired ? { reason: 'expired' } : {}, replace: true });
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const invalid = validateCode(code);
    if (invalid) {
      setError(invalid);
      return;
    }
    // Challenge perdu (rechargement de la page) : rien à vérifier, retour au login
    if (!challengeToken) {
      backToLogin(true);
      return;
    }

    setLoading(true);
    try {
      const { accessToken, admin, mustChangePassword } = await authApi.verifyMfa(challengeToken, code.trim());
      clearChallenge();
      setAuth(accessToken, { ...admin, mustChangePassword });
      // Mot de passe généré par un autre admin : changement avant toute autre page (RequireAuth le réimpose ensuite)
      void navigate({ to: mustChangePassword ? CHANGE_PASSWORD_PATH : '/', replace: true });
    } catch (err) {
      if (httpStatus(err) === 401 && serverMessage(err) === EXPIRED_CHALLENGE) {
        backToLogin(true);
        return;
      }
      setCode('');
      inputRef.current?.focus();
      if (httpStatus(err) === 401) {
        setError(serverMessage(err) === 'compte suspendu' ? 'Ce compte administrateur est suspendu' : 'Code invalide, réessayez');
      } else {
        setError(genericErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setBackupMode((b) => !b);
    setCode('');
    setError(null);
    inputRef.current?.focus();
  };

  return (
    <AuthCard
      icon={backupMode ? KeyRound : ShieldCheck}
      title="Vérification en deux étapes"
      description={
        backupMode ? (
          'Saisissez un de vos codes de secours. Chaque code ne fonctionne qu’une seule fois.'
        ) : (
          <>
            Saisissez le code à 6 chiffres affiché par votre application d’authentification
            {email && (
              <>
                {' '}pour <span className="font-medium text-foreground">{email}</span>
              </>
            )}
            .
          </>
        )
      }
      footer={
        <button type="button" onClick={() => backToLogin(false)} className="underline-offset-4 hover:text-foreground hover:underline">
          Revenir à la connexion
        </button>
      }
    >
      <form onSubmit={submit} noValidate className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="code">Code à 6 chiffres ou code de secours</Label>
          <Input
            ref={inputRef}
            id="code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoComplete="one-time-code"
            autoFocus
            inputMode={backupMode ? 'text' : 'numeric'}
            maxLength={9}
            placeholder={backupMode ? 'XXXX-XXXX' : '123456'}
            className="h-12 text-center font-mono text-xl tracking-[0.3em]"
            aria-invalid={Boolean(error)}
          />
        </div>
        {error && <FormAlert>{error}</FormAlert>}
        <Button type="submit" className="w-full" size="lg" disabled={loading}>
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
          {loading ? 'Vérification…' : 'Vérifier'}
        </Button>
        <Button type="button" variant="link" className="w-full text-brand-teal" onClick={toggleMode}>
          {backupMode ? 'Utiliser le code de l’application à la place' : 'Utiliser un code de secours à la place'}
        </Button>
      </form>
    </AuthCard>
  );
};
